/**
 * The relevance control: two bars, one per outcome, each its own span of 0–1.
 *
 * A fuzzy match rule produces a score between 0 and 1, and two things can
 * happen to a pair on the strength of it — it is merged, or it is put in front
 * of a person. Each outcome gets a bar spanning the whole 0–1 line with a
 * handle at each end: drag the minimum and the maximum to say which scores
 * lead to that outcome. The two spans are independent. Where they overlap,
 * merging wins, and the sentence under the bars says so.
 *
 * The numbers are editable as text as well as draggable. Stewards tune these
 * after looking at a queue, and "0.87, not 0.85" is not something a mouse does.
 *
 * `Bar` is a module-level component on purpose. Declared inside the track it
 * would be a new component type on every render, and React would unmount and
 * remount both bars — handles included — on every pointer move. That is the
 * shake a drag used to have.
 */

import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type RefObject } from 'react'

export interface Spans {
  /** Scores in [autoMerge, autoMergeMax] merge with nobody watching. */
  autoMerge: number
  autoMergeMax: number
  /** Scores in [review, reviewMax] wait for a person. */
  review: number
  reviewMax: number
}

interface Props extends Spans {
  onChange: (next: Spans) => void
  disabled?: boolean
}

type Tone = 'merge' | 'review'
type Grip = `${Tone}-min` | `${Tone}-max`

const STEP = 0.01

function clamp(value: number): number {
  if (Number.isNaN(value)) return 0
  return Math.min(1, Math.max(0, Math.round(value * 100) / 100))
}

const fmt = (value: number) => value.toFixed(2)
const pct = (value: number) => `${Math.round(value * 100)}%`

/** Where a pointer sits on a bar, as a 0–1 score. */
function scoreAt(element: HTMLDivElement | null, clientX: number): number {
  const rect = element?.getBoundingClientRect()
  if (!rect || !rect.width) return 0
  return clamp((clientX - rect.left) / rect.width)
}

interface BarProps {
  tone: Tone
  label: string
  hint: string
  from: number
  to: number
  barRef: RefObject<HTMLDivElement>
  disabled?: boolean
  onMove: (grip: Grip, value: number) => void
  onGrab: (grip: Grip) => void
}

/** One bar: a span of the 0–1 line, grabbed at either end. */
function Bar({ tone, label, hint, from, to, barRef, disabled, onMove, onGrab }: BarProps) {
  const minGrip: Grip = `${tone}-min`
  const maxGrip: Grip = `${tone}-max`
  const empty = to <= from

  function onKey(grip: Grip, current: number, event: KeyboardEvent) {
    const big = event.shiftKey ? 10 : 1
    if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
      event.preventDefault()
      onMove(grip, current - STEP * big)
    } else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
      event.preventDefault()
      onMove(grip, current + STEP * big)
    } else if (event.key === 'Home') {
      event.preventDefault()
      onMove(grip, 0)
    } else if (event.key === 'End') {
      event.preventDefault()
      onMove(grip, 1)
    }
  }

  return (
    <div className="rt-bar" data-tone={tone}>
      <div className="rt-barhead">
        <span className="rt-barname">{label}</span>
        <span className="rt-barhint">{hint}</span>
        <div className="rt-values">
          <label className="rt-value">
            <span className="rt-value-label">Min value</span>
            <input
              className="input"
              type="number"
              min={0}
              max={1}
              step={STEP}
              value={fmt(from)}
              disabled={disabled}
              aria-label={`${label}: minimum score`}
              onChange={(e) => onMove(minGrip, Number(e.target.value))}
            />
          </label>
          <label className="rt-value">
            <span className="rt-value-label">Max value</span>
            <input
              className="input"
              type="number"
              min={0}
              max={1}
              step={STEP}
              value={fmt(to)}
              disabled={disabled}
              aria-label={`${label}: maximum score`}
              onChange={(e) => onMove(maxGrip, Number(e.target.value))}
            />
          </label>
        </div>
      </div>

      <div
        className="rt-line"
        ref={barRef}
        onPointerDown={(event) => {
          if (disabled) return
          // Clicking the line moves whichever end is nearer, so a rough
          // adjustment does not require hitting a 14px handle.
          const score = scoreAt(barRef.current, event.clientX)
          const grip = Math.abs(score - from) <= Math.abs(score - to) ? minGrip : maxGrip
          onMove(grip, score)
          onGrab(grip)
        }}
      >
        <div className="rt-linebg" />
        <div
          className="rt-fill"
          data-empty={empty || undefined}
          style={{ left: pct(from), width: pct(Math.max(0, to - from)) }}
        />
        {(
          [
            [minGrip, from, `${label}: minimum score`],
            [maxGrip, to, `${label}: maximum score`],
          ] as [Grip, number, string][]
        ).map(([grip, value, description]) => (
          <button
            key={grip}
            type="button"
            className="rt-handle"
            data-end={grip.endsWith('min') ? 'min' : 'max'}
            style={{ left: pct(value) }}
            role="slider"
            aria-label={description}
            aria-valuemin={0}
            aria-valuemax={1}
            aria-valuenow={value}
            aria-valuetext={`${description}: ${fmt(value)}`}
            disabled={disabled}
            onPointerDown={(event) => {
              if (disabled) return
              event.stopPropagation()
              // Keep the pointer on this handle for the whole drag, and keep
              // focus off it — a focused button inside a scrolling modal is
              // what browsers scroll into view on every re-render.
              event.preventDefault()
              onGrab(grip)
            }}
            onKeyDown={(event) => onKey(grip, value, event)}
          >
            <span className="rt-handle-value" aria-hidden="true">
              {fmt(value)}
            </span>
          </button>
        ))}
      </div>

      <div className="rt-scale" aria-hidden="true">
        <span>0</span>
        <span>0.25</span>
        <span>0.50</span>
        <span>0.75</span>
        <span>1</span>
      </div>
    </div>
  )
}

export function RelevanceTrack({
  autoMerge,
  autoMergeMax,
  review,
  reviewMax,
  onChange,
  disabled,
}: Props) {
  const mergeRef = useRef<HTMLDivElement>(null)
  const reviewRef = useRef<HTMLDivElement>(null)
  const [dragging, setDragging] = useState<Grip | null>(null)

  // The latest spans, readable from inside a stable callback without the
  // callback itself changing identity on every value.
  const latest = useRef<Spans>({ autoMerge, autoMergeMax, review, reviewMax })
  latest.current = { autoMerge, autoMergeMax, review, reviewMax }

  /**
   * Move one grip. A minimum cannot pass its maximum and the reverse, so the
   * state this control can be left in is always a state the API accepts.
   */
  const move = useCallback(
    (grip: Grip, value: number) => {
      const v = clamp(value)
      const s = latest.current
      switch (grip) {
        case 'merge-min':
          onChange({ ...s, autoMerge: Math.min(v, s.autoMergeMax) })
          break
        case 'merge-max':
          onChange({ ...s, autoMergeMax: Math.max(v, s.autoMerge) })
          break
        case 'review-min':
          onChange({ ...s, review: Math.min(v, s.reviewMax) })
          break
        case 'review-max':
          onChange({ ...s, reviewMax: Math.max(v, s.review) })
          break
      }
    },
    [onChange],
  )

  useEffect(() => {
    if (!dragging) return
    const element = dragging.startsWith('merge') ? mergeRef.current : reviewRef.current
    const onMove = (event: PointerEvent) => move(dragging, scoreAt(element, event.clientX))
    const onUp = () => setDragging(null)
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
    }
  }, [dragging, move])

  const overlap = Math.max(review, autoMerge) < Math.min(reviewMax, autoMergeMax)
  const mergeEmpty = autoMergeMax <= autoMerge
  const reviewEmpty = reviewMax <= review

  return (
    <div className="rt" data-disabled={disabled || undefined} data-dragging={dragging || undefined}>
      <Bar
        tone="merge"
        label="Automatically merge"
        hint="No one is asked"
        from={autoMerge}
        to={autoMergeMax}
        barRef={mergeRef}
        disabled={disabled}
        onMove={move}
        onGrab={setDragging}
      />
      <Bar
        tone="review"
        label="Flag for review"
        hint="Waits for a person"
        from={review}
        to={reviewMax}
        barRef={reviewRef}
        disabled={disabled}
        onMove={move}
        onGrab={setDragging}
      />

      <p className="rt-sentence">
        {mergeEmpty ? (
          <>No score merges automatically. </>
        ) : (
          <>
            Scores from <b>{fmt(autoMerge)}</b> to <b>{fmt(autoMergeMax)}</b> merge
            automatically.{' '}
          </>
        )}
        {reviewEmpty ? (
          <>Nothing is flagged for review. </>
        ) : (
          <>
            Scores from <b>{fmt(review)}</b> to <b>{fmt(reviewMax)}</b> are flagged for review.{' '}
          </>
        )}
        {overlap && <>Where the two overlap, merging wins. </>}
        Anything else is not a match.
      </p>
    </div>
  )
}
